import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionPrivacy,
	ActionSubmit,
	ActionTags,
	ActionText,
	ActionValidator,
	RichMediaState,
} from '@bonusly/common-action-input';
import {
	Box,
	Center,
	Checkbox,
	Flex,
	Hide,
	HStack,
	InformationCircleSolid,
	LockClosedOutline,
	ModalBody,
	ModalFooter,
	rem,
	Spacer,
	Stack,
	Text,
	Tooltip,
} from '@bonusly/design-system';

import { RecipientSelectorWithCsvUpload } from '../../../../../../components/action-input/components/recipient/recipient-selector-with-csv-upload';
import { ActionModule } from '../../../../../../components/action-input/types';
import { Reveal } from '../../../../../../components/display/reveal';
import { useCurrentCompany } from '../../../../../../lib/who-am-i';
import { Award } from '../../../../../../types/bonus';
import { AwardHeader } from './AwardHeader';
import { useAwardModal, useAwardModalStore } from './hooks/useAwardModal';
import { useSharedAwardConfig } from './hooks/useSharedAwardConfig';
import { useTemplatePreview } from './hooks/useTemplatePreview';

type UneditableAwardInputProps = {
	award: Award;
	hideGiver: boolean;
	setHideGiver: (hideGiver: boolean) => void;
	setIsSubmitting: (isSubmitting: boolean) => void;
};

const validator = new ActionValidator({
	requireRecipient: true,
});

export const UneditableAwardInput: FC<UneditableAwardInputProps> = ({ award, hideGiver, setHideGiver, setIsSubmitting }) => {
	const { onClose } = useAwardModal();
	const { t } = useTranslation('recognition');

	const config = useSharedAwardConfig({ award, hideGiver, isTemplatedAward: true, onSuccess: onClose });
	const modules = useModules(award);

	const calculateTotalFn = (data: RichMediaState) => {
		const amount = (award.bonus_amount_changeable ? data.amount : award.amount) ?? 0;
		return data.recipientCount * amount;
	};

	const content = useSelectedRecipientContent();

	useEffect(() => {
		setIsSubmitting(config.isSubmitting);
	}, [config.isSubmitting, setIsSubmitting]);

	return (
		<ActionInput
			autofocus="end"
			content={content}
			context="templated-award"
			modules={modules}
			placeholder={t('awards.give_modal.receipientPlaceholder')}
			calculateTotalFn={calculateTotalFn}
			validator={validator}
			options={{
				recipient: {
					component: RecipientSelectorWithCsvUpload,
				},
			}}
			{...config}
		>
			<ModalBody py={4}>
				<Stack spacing={4}>
					<AwardHeader award={award} />

					<HStack>
						<ActionTags />
						<Spacer />
						<Hide below="xs">
							<ActionPrivacy />
						</Hide>
					</HStack>

					<ActionText inputStyles={{ minHeight: rem(75) }} />

					<Reveal if={award.amount}>
						<HStack>
							<Text fontWeight="medium" fontSize="lg">
								Required point amount:{' '}
								<Box as="span" color="points.fgOnSurface">
									+{award.amount}
								</Box>{' '}
							</Text>

							<Center bgColor="secondary.bgAlpha" borderRadius="full" boxSize={rem(32)} display="inline-flex">
								<LockClosedOutline size="xs" />
							</Center>
						</HStack>
					</Reveal>

					<Text fontSize="lg">
						<Box as="span" fontWeight="medium">
							{t('awards.give_modal.template_label')}:
						</Box>{' '}
						{useTemplatePreview(award)}{' '}
						<Tooltip label={t('awards.give_modal.template_tooltip_custom_message')}>
							<Center bgColor="secondary.bgAlpha" borderRadius="full" boxSize={rem(32)} display="inline-flex">
								<LockClosedOutline size="xs" />
							</Center>
						</Tooltip>
					</Text>

					<Box>
						<Checkbox isChecked={!hideGiver} onChange={(event) => setHideGiver(!event.target.checked)}>
							<Text mt={1}>{t('awards.give_modal.show_giver_label')}</Text>
						</Checkbox>
					</Box>

					<ActionFeedback />
					<ActionMedia w="full" />
				</Stack>
			</ModalBody>

			<ModalFooter borderTop="1px solid" borderColor="gray.100" flexDirection="column" alignItems="initial">
				<Reveal if={award?.approval_process}>
					<Flex bg="gray.25" borderRadius="md" direction="row" px={3} py={2} mb={4}>
						<InformationCircleSolid color="gray.500" />

						<Box ml={2}>
							<Text fontWeight="semibold" color="gray.900" fontSize="sm">
								{t('recognition:awards.give_modal.requires_approval_title')}
							</Text>
							<Text fontWeight="normal" color="gray.900" fontSize="sm">
								{t('recognition:awards.give_modal.requires_approval_description')}
							</Text>
						</Box>
					</Flex>
				</Reveal>

				<Flex
					alignItems={{ base: 'stretch', xs: 'flex-start' }}
					direction={{ base: 'column', xs: 'row' }}
					gap={2}
					justifyContent="space-between"
					mb={2}
					w="full"
				>
					<Flex justifyContent="space-between" flexGrow={1}>
						<ActionDecorations />
						<Hide above="xs">
							<ActionPrivacy />
						</Hide>
					</Flex>

					<ActionSubmit size="lg" />
				</Flex>
			</ModalFooter>
		</ActionInput>
	);
};

// Unline the editiable award input, the "content" we show in the input is only used for entering the award recipients
// and possibly the amount (if it's editable). We don't have to worry about parsing message content since that's displayed
// in the template below the recipient input.
const useSelectedRecipientContent = () => {
	const recipient = useAwardModalStore((state) => state.recipient);

	if (!recipient) {
		return null;
	}

	return {
		type: 'doc',
		content: [
			{
				type: 'paragraph',
				content: [
					{
						type: 'recipient',
						attrs: {
							id: recipient.username,
							record: {
								id: recipient.id,
								displayName: recipient.displayName,
								imageSrc: recipient.profilePic,
								username: recipient.username,
							},
						},
					},
					{
						type: 'text',
						text: ' ',
					},
				],
			},
		],
	};
};

const useModules = (award: Award) => {
	const company = useCurrentCompany();

	const gifsEnabled = !company.disable_gifs;
	const imagesEnabled = !company.disable_image_uploads;
	const uploadEnabled = !company.disable_video_uploads;
	const recordEnabled = !company.disable_video_recordings;

	const modules: Array<ActionModule | boolean | undefined> = [
		'recipient',
		'privacy',
		gifsEnabled && 'gif',
		uploadEnabled && 'attachment',
		recordEnabled && 'video-recording',
		award.bonus_amount_changeable && 'amount',
		!uploadEnabled && imagesEnabled && 'image',
	];

	return modules.filter(Boolean) as ActionModule[];
};
